import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useUser } from "../context/UserContext";
import { SessionContext } from "../context/sessionContext";
import { Box, CircularProgress, LinearProgress } from "@mui/material";
import { Search, LayoutGrid, List, MoreVertical, Plus } from "lucide-react";
import {
  readPendingGenerateDraft,
  resumeRouteForDraft,
} from "../utils/surveyFlowStorage";

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
const PROJECT_COMPLETION_DURATION_MS =
  ENVIRONMENT === "production" ? 36 * 60 * 60 * 1000 : 2 * 60 * 60 * 1000;

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getTimeRemaining = (createdAt) => {
  if (!createdAt) return null;
  const completionTime = new Date(
    new Date(createdAt).getTime() + PROJECT_COMPLETION_DURATION_MS,
  );
  const remaining = completionTime - new Date();
  if (remaining <= 0) return "Complete";
  const h = Math.floor(remaining / 3_600_000);
  const m = Math.floor((remaining % 3_600_000) / 60_000);
  return h > 0 ? `~${h}h ${m}m remaining` : `~${m}m remaining`;
};

const getProgressPct = (createdAt) => {
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  const elapsed = Date.now() - created.getTime();
  return Math.min((elapsed / PROJECT_COMPLETION_DURATION_MS) * 100, 100);
};

const VIEW_WINDOW_DAYS = 7;
const EXPIRING_PLANS = ["Freemium", "Custom"];
const isViewExpired = (project, planName) => {
  if (!EXPIRING_PLANS.includes(planName)) return false;
  if (project?.is_premade) return false;
  if (!project?.created_at) return false;
  const expiresAt =
    new Date(project.created_at).getTime() + VIEW_WINDOW_DAYS * 24 * 3_600_000;
  return Date.now() > expiresAt;
};
const daysRemaining = (createdAt) => {
  if (!createdAt) return null;
  const expiresAt =
    new Date(createdAt).getTime() + VIEW_WINDOW_DAYS * 24 * 3_600_000;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / (24 * 3_600_000)));
};

export default function InsightsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedUserId = queryParams.get("user_id");

  const { userData, isAdmin, roleName } = useUser();
  const { getUserId } = useContext(SessionContext);

  const isPaymentPending = userData?.planDetails?.paymentStatus === "pending";
  const canCreateProject =
    !isPaymentPending && (userData?.planDetails?.total_project_limits ?? 1) > 0;

  let memberName =
    location.state?.memberName || queryParams.get("name") || userData?.username;

  const [projects, setProjects] = useState([]);
  const [generatingProjects, setGeneratingProjects] = useState([]);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [planName, setPlanName] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [viewMode, setViewMode] = useState("grid");

  const isCollaborator = roleName === "Collaborator";
  const subtitle = isCollaborator
    ? "View and collaborate on projects you have been added to."
    : "Manage and create your insight projects";

  useEffect(() => {
    const fetchProjects = async (isInitialLoad = true) => {
      try {
        const token = Cookies.get("token");
        const user_id = selectedUserId || getUserId();
        if (!token || !user_id) return;

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/project/listAllSurveyProject`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", token },
            body: JSON.stringify({ user_id, role: roleName }),
          },
        );
        const data = await response.json();

        if (data?.planName !== undefined) {
          setPlanName(data.planName);
        }

        if (Array.isArray(data?.data)) {
          const valid = data.data.filter(
            (p) => p.project_name || p.survey_name,
          );
          const now = Date.now();
          const completed = [];
          const generating = [];

          valid.forEach((proj) => {
            if (proj.is_premade) {
              completed.push(proj);
              return;
            }
            if (proj.created_at) {
              const completionTime =
                new Date(proj.created_at).getTime() +
                PROJECT_COMPLETION_DURATION_MS;
              now < completionTime
                ? generating.push(proj)
                : completed.push(proj);
            } else {
              completed.push(proj);
            }
          });

          setProjects(completed);
          setGeneratingProjects(generating);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    fetchProjects(true);
    let interval = setInterval(() => fetchProjects(false), 10_000);
    const slowSwitch = setTimeout(() => {
      clearInterval(interval);
      interval = setInterval(() => fetchProjects(false), 30_000);
    }, 60_000);

    return () => {
      clearInterval(interval);
      clearTimeout(slowSwitch);
    };
  }, [getUserId]);
  useEffect(() => {
    if (roleName === "Collaborator") {
      setPendingDraft(null);
      return;
    }
    const user_id = selectedUserId || getUserId();
    setPendingDraft(readPendingGenerateDraft(user_id));
  }, [roleName, selectedUserId, getUserId]);

  const handleCreateNewProject = () => navigate("/create-survey");

  const [payLoading, setPayLoading] = useState(false);
  const handlePayNow = async () => {
    setPayLoading(true);
    try {
      const token = Cookies.get("token");
      localStorage.setItem(
        "paymentContext",
        JSON.stringify({ source: "CUSTOM_INVITE" }),
      );
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token },
          credentials: "include",
          body: JSON.stringify({ purpose: "custom_account_activation" }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to start payment");
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("Pay Now error:", err);
      setPayLoading(false);
    }
  };

  const handleResumeDraft = () => {
    const route = resumeRouteForDraft(pendingDraft);
    if (!route) return;
    navigate(route.path, { state: route.state });
  };

  const handleViewInsights = (project) => {
    if (isViewExpired(project, planName)) return;
    const s3 = project.artifacts?.s3_paths || {};
    const toFiles = (obj) =>
      Object.entries(obj)
        .filter(([, path]) => path)
        .map(([label, path]) => ({
          name: label.replace(/_/g, " "),
          key: path,
          filePath: path,
        }));

    navigate(`/projects/view/${project._id}`, {
      state: {
        surveyId: project.survey_id,
        audienceGroupId: project.audience_grp_id,
        projectName: project.project_name || project.survey_name,
        createdAt: project.created_at,
        bannerBookFiles: toFiles(s3.bannerbook || {}),
        researchFiles: toFiles(s3.report || {}),
        surveyFiles: toFiles(s3.survey || {}),
        bannerImagePath: project.header_url,
        fullProject: project,
      },
    });
  };

  const handleGeneratingClick = (project) =>
    navigate("/generate-insights", {
      state: {
        projectId: project._id,
        surveyId: project.survey_id,
        audienceGroupId: project.audience_grp_id,
        surveyName: project.project_name || project.survey_name,
        createdAt: project.created_at,
      },
    });
  const allCompleted = useMemo(() => {
    let list = [...projects];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        (p.project_name || p.survey_name || "").toLowerCase().includes(q),
      );
    }
    if (sortBy === "name") {
      list.sort((a, b) =>
        (a.project_name || a.survey_name || "").localeCompare(
          b.project_name || b.survey_name || "",
        ),
      );
    } else if (sortBy === "oldest") {
      list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return list;
  }, [projects, search, sortBy]);

  const showingCount =
    allCompleted.length +
    generatingProjects.length +
    (roleName !== "Collaborator" && canCreateProject ? 1 : 0);

  return (
    <div className="gi-projects">
      <div className="gi-projects__header">
        <div>
          <h1 className="gi-projects__title">Projects</h1>
          <p className="gi-projects__subtitle">{subtitle}</p>
        </div>
        {roleName !== "Collaborator" && canCreateProject && (
          <button
            className="gi-btn gi-btn--primary"
            onClick={handleCreateNewProject}
          >
            <Plus size={16} />
            Create New Project
          </button>
        )}
      </div>
      <div className="gi-projects__toolbar">
        <div className="gi-search">
          <Search size={15} className="gi-search__icon" />
          <input
            className="gi-search__input"
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="gi-toolbar-right">
          <select
            className="gi-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Sort by: Recent</option>
            <option value="oldest">Sort by: Oldest</option>
            <option value="name">Sort by: Name</option>
          </select>

          <div className="gi-view-toggle">
            <button
              className={`gi-view-toggle__btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`gi-view-toggle__btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="gi-projects__loader">
          <CircularProgress size={36} style={{ color: "#3b82f6" }} />
        </div>
      ) : (
        <>
          {viewMode === "grid" && (
            <div className="gi-grid">
              {roleName !== "Collaborator" && canCreateProject && (
                <div
                  className="gi-card gi-card--create"
                  onClick={handleCreateNewProject}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleCreateNewProject()
                  }
                >
                  <div className="gi-card--create__inner">
                    <div className="gi-card--create__icon">
                      <Plus size={28} />
                    </div>
                    <span>Create New Project</span>
                    <p className="gi-card--create__sub">
                      Start a new insight project
                    </p>
                  </div>
                </div>
              )}
              {pendingDraft && (
                <div
                  className="gi-card gi-card--resume"
                  onClick={handleResumeDraft}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleResumeDraft()}
                >
                  <div className="gi-resume-badge">Incomplete</div>
                  <div className="gi-card__img gi-card__img--placeholder">
                    <div className="gi-placeholder-spinner" />
                  </div>
                  <div className="gi-card__body">
                    <p className="gi-card__name">
                      {pendingDraft.surveyName || "Survey in progress"}
                    </p>
                    <p className="gi-resume-message">
                      Survey generation in progress — please complete it to move
                      ahead.
                    </p>
                    <span className="gi-resume-cta">Resume →</span>
                  </div>
                </div>
              )}
              {generatingProjects.map((project) => {
                const name =
                  project.project_name || project.survey_name || "Untitled";
                const progress = getProgressPct(project.created_at);
                return (
                  <div
                    key={project._id}
                    className="gi-card gi-card--generating"
                    onClick={() => handleGeneratingClick(project)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="gi-generating-badge">
                      <CircularProgress size={10} style={{ color: "#fff" }} />
                      Generating
                    </div>

                    {project.image_generation_status === "completed" ? (
                      <img
                        className="gi-card__img"
                        src={
                          project.thumbnail_url ||
                          "/json-media/img/defaultProject.png"
                        }
                        alt={name}
                      />
                    ) : (
                      <div className="gi-card__img gi-card__img--placeholder">
                        <div className="gi-placeholder-spinner" />
                      </div>
                    )}

                    <div className="gi-card__body">
                      <p className="gi-card__name">{name}</p>
                      <span className="gi-card__date">
                        {formatDate(project.created_at)}
                      </span>
                      <p className="gi-card__timer">
                        {getTimeRemaining(project.created_at)}
                      </p>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        className="gi-progress"
                      />
                    </div>
                  </div>
                );
              })}
              {allCompleted.map((project) => {
                const name =
                  project.project_name || project.survey_name || "Untitled";
                const expired = isViewExpired(project, planName);
                return (
                  <div
                    key={project._id}
                    className={`gi-card ${expired ? "gi-card--expired" : ""}`}
                    onClick={() => handleViewInsights(project)}
                    role="button"
                    tabIndex={0}
                    aria-disabled={expired}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleViewInsights(project)
                    }
                  >
                    {isCollaborator && (
                      <div className="gi-collab-badge">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                        </svg>
                        Collaborator
                      </div>
                    )}
                    {project.is_premade && !expired && (
                      <div className="gi-sample-badge">Sample</div>
                    )}
                    {expired && (
                      <div className="gi-expired-overlay">
                        <span className="gi-expired-badge"> View Expired</span>
                        <span className="gi-expired-sub">
                          Contact support to restore access
                        </span>
                      </div>
                    )}
                    <img
                      className="gi-card__img"
                      src={
                        project.thumbnail_url ||
                        "/json-media/img/defaultProject.png"
                      }
                      alt={name}
                      style={
                        expired
                          ? { filter: "grayscale(1)", opacity: 0.5 }
                          : undefined
                      }
                    />
                    <div className="gi-card__body gi-card__body--row">
                      <div>
                        <p className="gi-card__name">{name}</p>
                        <span className="gi-card__date">
                          {formatDate(project.created_at)}
                        </span>
                        {!expired &&
                          !project.is_premade &&
                          EXPIRING_PLANS.includes(planName) && (
                            <p className="gi-card__expiry-note">
                              {daysRemaining(project.created_at)}d left to view
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {allCompleted.length === 0 &&
                generatingProjects.length === 0 &&
                !pendingDraft && (
                  <div className="gi-empty">
                    {search ? (
                      `No projects match "${search}".`
                    ) : isPaymentPending ? (
                      <div className="gi-payment-pending-empty">
                        <p>
                          Your payment hasn't been completed yet. Complete your
                          payment to unlock project creation.
                        </p>
                        <button
                          className="gi-btn gi-btn--primary"
                          onClick={handlePayNow}
                          disabled={payLoading}
                        >
                          {payLoading ? "Redirecting..." : "Pay Now"}
                        </button>
                      </div>
                    ) : (
                      "No projects available."
                    )}
                  </div>
                )}
            </div>
          )}
          {viewMode === "list" && (
            <div className="gi-list">
              <div className="gi-list__header">
                <span className="gi-list__col gi-list__col--project">
                  Project
                </span>
                <span className="gi-list__col">Date Added ↓</span>
                <span className="gi-list__col">Last Updated</span>
                <span className="gi-list__col gi-list__col--action" />
              </div>
              {pendingDraft && (
                <div
                  className="gi-list__row"
                  onClick={handleResumeDraft}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleResumeDraft()}
                >
                  <div className="gi-list__col gi-list__col--project">
                    <div className="gi-list__thumb gi-card__img--placeholder">
                      <div className="gi-placeholder-spinner" />
                    </div>
                    <span className="gi-list__name">
                      {pendingDraft.surveyName || "Survey in progress"}
                    </span>
                    <span
                      className="gi-resume-badge"
                      style={{ position: "static" }}
                    >
                      Incomplete
                    </span>
                  </div>
                  <span className="gi-list__col gi-list__col--date">
                    {formatDate(pendingDraft.updatedAt)}
                  </span>
                  <span className="gi-list__col gi-list__col--date">
                    Resume →
                  </span>
                </div>
              )}
              {generatingProjects.map((project) => {
                const name =
                  project.project_name || project.survey_name || "Untitled";
                return (
                  <div
                    key={project._id}
                    className="gi-list__row"
                    onClick={() => handleGeneratingClick(project)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="gi-list__col gi-list__col--project">
                      <img
                        className="gi-list__thumb"
                        src={
                          project.thumbnail_url ||
                          "/json-media/img/defaultProject.png"
                        }
                        alt={name}
                      />
                      <span className="gi-list__name">{name}</span>
                      <span className="gi-generating-badge gi-generating-badge--inline">
                        <CircularProgress size={8} style={{ color: "#fff" }} />
                        Generating
                      </span>
                    </div>
                    <span className="gi-list__col gi-list__col--date">
                      {formatDate(project.created_at)}
                    </span>
                    <span className="gi-list__col gi-list__col--date">
                      {formatDate(project.created_at)}
                    </span>
                  </div>
                );
              })}
              {allCompleted.map((project) => {
                const name =
                  project.project_name || project.survey_name || "Untitled";
                const expired = isViewExpired(project, planName);
                return (
                  <div
                    key={project._id}
                    className={`gi-list__row ${expired ? "gi-list__row--expired" : ""}`}
                    onClick={() => handleViewInsights(project)}
                    role="button"
                    tabIndex={0}
                    aria-disabled={expired}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleViewInsights(project)
                    }
                  >
                    <div className="gi-list__col gi-list__col--project">
                      <img
                        className="gi-list__thumb"
                        src={
                          project.thumbnail_url ||
                          "/json-media/img/defaultProject.png"
                        }
                        alt={name}
                        style={
                          expired
                            ? { filter: "grayscale(1)", opacity: 0.5 }
                            : undefined
                        }
                      />
                      <span className="gi-list__name">{name}</span>
                      {project.is_premade && (
                        <span className="gi-generating-badge gi-generating-badge--inline gi-sample-badge--inline">
                          Sample
                        </span>
                      )}
                      {expired && (
                        <span className="gi-generating-badge gi-generating-badge--inline gi-expired-badge--inline">
                          Expired
                        </span>
                      )}
                    </div>
                    <span className="gi-list__col gi-list__col--date">
                      {formatDate(project.created_at)}
                    </span>
                    <span className="gi-list__col gi-list__col--date">
                      {expired
                        ? "Access expired"
                        : formatDate(project.updated_at || project.created_at)}
                    </span>
                  </div>
                );
              })}

              {allCompleted.length === 0 &&
                generatingProjects.length === 0 &&
                !pendingDraft && (
                  <div className="gi-empty gi-empty--list">
                    {search ? (
                      `No projects match "${search}".`
                    ) : isPaymentPending ? (
                      <div className="gi-payment-pending-empty">
                        <p>
                          Your payment hasn't been completed yet. Complete your
                          payment to unlock project creation.
                        </p>
                        <button
                          className="gi-btn gi-btn--primary"
                          onClick={handlePayNow}
                          disabled={payLoading}
                        >
                          {payLoading ? "Redirecting..." : "Pay Now"}
                        </button>
                      </div>
                    ) : (
                      "No projects available."
                    )}
                  </div>
                )}
              {(allCompleted.length > 0 ||
                generatingProjects.length > 0 ||
                pendingDraft) && (
                <div className="gi-list__footer">
                  Showing 1 to{" "}
                  {allCompleted.length +
                    generatingProjects.length +
                    (pendingDraft ? 1 : 0)}{" "}
                  of{" "}
                  {allCompleted.length +
                    generatingProjects.length +
                    (pendingDraft ? 1 : 0)}{" "}
                  projects
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
